// Thin top brand bar for the authed app — replaces the marketing SiteHeader on
// authed routes. Brand → dashboard, an optional "find a device manual" search
// trigger (boat routes only), and an account menu (Profile, Sign out).

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth, useDemoGuest } from '../../auth';
import { markDemoGuest } from '../../lib/demo';
import { useLeaveDemo } from '../../lib/useLeaveDemo';
import { LogoMark } from '../ui/Icon';
import { SearchIcon } from './icons';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

export function BrandBar({ onOpenSearch }: { onOpenSearch?: () => void }) {
  const auth = useAuth();
  // A Try it guest has no linked devices (TOM-868).
  const guest = useDemoGuest();
  const leaveDemo = useLeaveDemo();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const fullName = auth.user?.displayName ?? auth.user?.email ?? 'Account';
  const email = auth.user?.email ?? null;

  async function onLogout() {
    setMenuOpen(false);
    // A Try it guest's Sign out also ends its lease (TOM-868).
    if (guest) {
      await leaveDemo();
      return;
    }
    markDemoGuest(false);
    await auth.logout();
    navigate('/');
  }

  return (
    <header className="brand-bar">
      <Link className="brand-logo" to="/dashboard" aria-label="Neomaris — dashboard">
        <span className="brand-mark" aria-hidden="true">
          <LogoMark />
        </span>
        Neomaris
      </Link>
      <span className="brand-spacer" />
      {onOpenSearch ? (
        <button type="button" className="kbd-hint" onClick={onOpenSearch}>
          <SearchIcon />
          Find a device manual
          <kbd>⌘</kbd>
          <kbd>K</kbd>
        </button>
      ) : null}
      <div className="account-menu" ref={menuRef}>
        <button
          type="button"
          className="account-btn"
          onClick={() => setMenuOpen((o) => !o)}
          aria-haspopup="true"
          aria-expanded={menuOpen}
          aria-label={`Account: ${fullName}`}
          title={fullName}
        >
          <span className="avatar" aria-hidden="true">
            {initialsOf(fullName)}
          </span>
        </button>
        <div className={`account-pop${menuOpen ? ' open' : ''}`} role="menu">
          <div className="account-pop-head" aria-hidden="true">
            <span className="account-pop-name">{fullName}</span>
            {email && email !== fullName ? (
              <span className="account-pop-email">{email}</span>
            ) : null}
          </div>
          <hr />
          <Link to="/profile" role="menuitem" onClick={() => setMenuOpen(false)}>
            Profile
          </Link>
          {guest ? null : (
            <Link to="/devices" role="menuitem" onClick={() => setMenuOpen(false)}>
              Linked devices
            </Link>
          )}
          <hr />
          <button type="button" role="menuitem" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
